import { useState, useEffect, useRef, Suspense, lazy } from 'react';
import axios from 'axios';
import { createBrowserRouter, RouterProvider, createRoutesFromElements, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import { isTokenExpired, scheduleTokenExpiryLogout, clearAuthData } from './utils/auth';
import './App.css';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const PartnerRegistrations = lazy(() => import('./pages/PartnerRegistrations'));
const PartnerLogo = lazy(() => import('./pages/PartnerLogo'));
const Users = lazy(() => import('./pages/Users'));
const Services = lazy(() => import('./pages/Services'));
const Servers = lazy(() => import('./pages/Servers'));
const Categories = lazy(() => import('./pages/Categories'));
const Purchases = lazy(() => import('./pages/Purchases'));
const Transactions = lazy(() => import('./pages/Transactions'));
const ServiceRequests = lazy(() => import('./pages/ServiceRequests'));
const Discounts = lazy(() => import('./pages/Discounts'));
const Login = lazy(() => import('./pages/Login'));
const Layout = lazy(() => import('./components/Layout'));

// Create a client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error: any) => {
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const expiryTimeoutRef = useRef<number | null>(null);

  // Clear any existing timeout when component unmounts
  useEffect(() => {
    return () => {
      if (expiryTimeoutRef.current) {
        window.clearTimeout(expiryTimeoutRef.current);
        expiryTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('adminAccessToken');
        console.log('🔍 Checking admin auth, token exists:', !!token);
        
        if (!token) {
          console.log('❌ No admin token found');
          if (isMounted) {
            setIsAuthenticated(false);
            setLoading(false);
          }
          return;
        }

        // Check if token is expired
        if (isTokenExpired(token)) {
          console.warn('🚨 Admin token is expired, clearing auth');
          clearAuthData();
          if (isMounted) {
            setIsAuthenticated(false);
            setLoading(false);
          }
          return;
        }

        // Token exists and is not expired - user is authenticated
        console.log('✅ Admin token is valid');
        
        // Schedule automatic logout when token expires
        if (expiryTimeoutRef.current) {
          window.clearTimeout(expiryTimeoutRef.current);
        }
        expiryTimeoutRef.current = scheduleTokenExpiryLogout(token);

        // Set axios default authorization header
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // Add response interceptor to auto-logout on 401/403
          const interceptorId = axios.interceptors.response.use(
            (res) => res,
            (err) => {
              const status = err?.response?.status;
              if (status === 401 || status === 403) {
                console.warn('🚨 API returned 401/403 — clearing auth and redirecting to login');
                clearAuthData();
                if (expiryTimeoutRef.current) {
                  window.clearTimeout(expiryTimeoutRef.current);
                  expiryTimeoutRef.current = null;
                }
                window.location.replace('/login');
              }
              return Promise.reject(err);
            }
          );

          // Store interceptor id on window so we can eject it later
          (window as any).__axiosAdminInterceptorId = interceptorId;
        }

        if (isMounted) {
          setIsAuthenticated(true);
        }
      } catch (error: any) {
        if (isMounted) {
          console.error('❌ Admin auth check failed:', error);
          clearAuthData();
          if (expiryTimeoutRef.current) {
            window.clearTimeout(expiryTimeoutRef.current);
            expiryTimeoutRef.current = null;
          }
          setIsAuthenticated(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      // Eject axios interceptor on unmount
      const id = (window as any).__axiosAdminInterceptorId;
      if (typeof id !== 'undefined' && id !== null) {
        axios.interceptors.response.eject(id);
        delete (window as any).__axiosAdminInterceptorId;
      }
    };
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route
          path="/login"
          element={
            isAuthenticated ?
            <Navigate to="/dashboard" replace /> :
            <Suspense fallback={<LoadingSpinner />}>
              <Login onLogin={() => setIsAuthenticated(true)} />
            </Suspense>
          }
        />
        <Route
          path="/*"
          element={
            isAuthenticated ? (
              <Suspense fallback={<LoadingSpinner />}>
                <Layout onLogout={() => setIsAuthenticated(false)} />
              </Suspense>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="partner-registrations" element={<PartnerRegistrations />} />
          <Route path="partner-logo" element={<PartnerLogo />} />
          <Route path="users" element={<Users />} />
          <Route path="services" element={<Services />} />
          <Route path="servers" element={<Servers />} />
          <Route path="categories" element={<Categories />} />
          <Route path="purchases" element={<Purchases />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="service-requests" element={<ServiceRequests />} />
          <Route path="discounts" element={<Discounts />} />
        </Route>
      </>
    ),
    { future: { v7_startTransition: true, v7_relativeSplatPath: true } } as any
  );

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
